import { useId, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { Sheet } from '../components/Sheet'
import { CameraIcon, GridIcon, UploadArrowIcon, VideoIcon } from '../components/icons'
import { uploadQueue } from '../upload/queue'
import { prefersWebcamSheet } from '../lib/device'
import { WebcamSheet } from './WebcamSheet'

/** Wat de bestandskiezer aanbiedt. HEIC staat erbij omdat iOS dat levert. */
const accept = 'image/*,video/*,.heic,.heif'

/**
 * Wat de camera aanbiedt: één soort per knop.
 *
 * Hier zaten twee dingen fout. Eerst stonden de bestandsextensies er nog in
 * (`.heic,.heif`); staat er een extensie in `accept`, dan negeren Android
 * Chrome en iOS Safari `capture` en openen ze alsnog de kiezer. Die zijn eruit,
 * en het bleef gebeuren — want ook `image/*,video/*` samen is te vaag. Het
 * toestel moet weten in welke stand de camera open moet, foto of video, en met
 * allebei erin weet het dat niet. Dan valt het terug op wat het altijd kan: de
 * bestandskiezer.
 *
 * Vandaar twee knoppen in plaats van één. Dat is meteen eerlijker naar wat er
 * daarna gebeurt: de camera-app opent óf in fotostand óf in videostand, dus die
 * keuze maak je hoe dan ook — nu vooraf en zichtbaar in plaats van erna in een
 * scherm dat wij niet gemaakt hebben.
 *
 * De extensies zijn hier ook nergens voor nodig: een foto van een iPhone komt
 * binnen als `image/heic` en valt gewoon onder `image/*`.
 */
const photoCapture = 'image/*'
const videoCapture = 'video/*'

/**
 * Ronde 8A en 9A — "Wat wil je delen?".
 *
 * Op mobiel twee rijen: de camera van het toestel en de bestandskiezer. Beide
 * zijn een `<input type="file">`; de camera-app en de fotokiezer uit ronde 8B
 * en 8C zijn de systeemschermen van de telefoon en die bouwen we niet na — ze
 * zijn beter dan wat een webpagina ervan kan maken en de gast kent ze al.
 *
 * Op desktop krijgt hetzelfde paneel er een sleepzone bij, want daar is slepen
 * de manier waarop mensen bestanden aanleveren.
 */
export function AddMediaPanel({
  open,
  onClose,
  challengeId = null,
}: {
  open: boolean
  onClose: () => void

  /** Zet dit erbij, dan hangt wat je uploadt meteen aan die challenge (22E). */
  challengeId?: string | null
}) {
  const headingId = useId()
  const photo = useRef<HTMLInputElement>(null)
  const video = useRef<HTMLInputElement>(null)
  const files = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)
  const [webcam, setWebcam] = useState(false)

  async function accepted(list: FileList | File[] | null) {
    const chosen = [...(list ?? [])]

    if (chosen.length === 0) {
      return
    }

    onClose()

    await uploadQueue.add(chosen, challengeId)
  }

  /**
   * Klikt de verborgen input aan die bij deze knop hoort.
   *
   * Op een machine met een muis heeft dat geen zin — daar negeert de browser
   * `capture` en krijg je de bestandskiezer, precies wat deze knop niet hoort
   * te doen. Dan nemen we onze eigen webcam. Dat kan op een smal scherm ook
   * gebeuren: een laptopvenster van 500px breed is geen telefoon.
   */
  function openCamera(input: { current: HTMLInputElement | null }) {
    if (prefersWebcamSheet()) {
      setWebcam(true)

      return
    }

    input.current?.click()
  }

  function onDrop(event: DragEvent) {
    event.preventDefault()
    setDragging(false)

    void accepted(event.dataTransfer.files)
  }

  return (
    <Sheet open={open} onClose={onClose} labelledBy={headingId} width="lg">
      <div className="flex items-start justify-between gap-5">
        <div>
          <h2 id={headingId} className="font-display text-[30px] leading-[1.15] sm:text-[36px]">
            Wat wil je delen?
          </h2>
          <p className="text-ink-soft mt-1.5 text-sm sm:mt-2 sm:text-[15px]">
            Foto’s en video’s komen meteen in het album.
          </p>
        </div>

        <button
          type="button"
          aria-label="Sluiten"
          onClick={onClose}
          className="border-line-warm text-ink-muted hidden size-9 shrink-0 items-center justify-center rounded-full border text-[15px] sm:flex"
        >
          ✕
        </button>
      </div>

      {/* Alleen desktop: op een telefoon valt er niets te slepen. */}
      <div
        onDragOver={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={
          'rounded-card bg-surface mt-6.5 hidden border-[1.5px] border-dashed px-6 py-11.5 text-center sm:block ' +
          (dragging ? 'border-accent bg-tint/40' : 'border-line')
        }
      >
        <div className="bg-tint text-accent mx-auto mb-4 flex size-13.5 items-center justify-center rounded-2xl">
          <UploadArrowIcon className="size-5.5" />
        </div>

        <p className="text-lg font-semibold">Sleep je foto’s hierheen</p>
        <p className="text-ink-soft mt-1.5 text-sm">JPG, PNG, HEIC of MP4 · meerdere tegelijk</p>

        <button
          type="button"
          onClick={() => files.current?.click()}
          className="bg-accent text-accent-ink mt-5 inline-flex h-12 items-center rounded-full px-6 text-base font-semibold"
        >
          Bestanden kiezen
        </button>
      </div>

      <div className="my-5.5 hidden items-center gap-4 sm:flex">
        <span className="bg-line-card h-px flex-1" />
        <span className="text-ink-faint text-[13px] tracking-[0.16em] uppercase">of</span>
        <span className="bg-line-card h-px flex-1" />
      </div>

      <div className="mt-5 flex flex-col gap-2.5 sm:mt-0">
        {/* Op desktop één regel voor de webcam: daar doet `capture` niets en
            kiest onze eigen sheet zelf tussen foto en video. Op een telefoon
            twee regels, één per stand van de camera-app. */}
        <div className="hidden sm:block">
          <Choice
            title="Webcam gebruiken"
            detail="Neem meteen een foto of video op"
            icon={<CameraIcon className="size-5" />}
            onClick={() => setWebcam(true)}
          />
        </div>

        <div className="flex flex-col gap-2.5 sm:hidden">
          <Choice
            title="Foto maken"
            detail="Opent de camera van je toestel"
            icon={<CameraIcon className="size-5" />}
            onClick={() => openCamera(photo)}
          />

          <Choice
            title="Video opnemen"
            detail="Opent de camera in videostand"
            icon={<VideoIcon className="size-5" />}
            onClick={() => openCamera(video)}
          />

          <Choice
            title="Mijn bestanden"
            detail="Kies meerdere uit je toestel"
            icon={<GridIcon className="size-4.5" />}
            onClick={() => files.current?.click()}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="text-ink-soft mt-4.5 w-full py-1 text-center text-[15px] sm:hidden"
      >
        Annuleren
      </button>

      {/* Twee aparte inputs, want `capture` heeft één soort nodig om de camera
          in de juiste stand te openen — zie de uitleg bovenaan. Geen `multiple`
          erbij: dat zet `capture` opnieuw buitenspel. */}
      <input
        ref={photo}
        type="file"
        accept={photoCapture}
        capture="environment"
        className="hidden"
        onChange={(event) => void accepted(event.target.files)}
      />

      <input
        ref={video}
        type="file"
        accept={videoCapture}
        capture="environment"
        className="hidden"
        onChange={(event) => void accepted(event.target.files)}
      />

      <input
        ref={files}
        type="file"
        accept={accept}
        multiple
        className="hidden"
        onChange={(event) => void accepted(event.target.files)}
      />

      <WebcamSheet
        open={webcam}
        onClose={() => setWebcam(false)}
        onCapture={(file) => {
          setWebcam(false)
          void accepted([file])
        }}
      />
    </Sheet>
  )
}

function Choice({
  title,
  detail,
  desktopTitle,
  desktopDetail,
  icon,
  onClick,
}: {
  title: string
  detail: string
  desktopTitle?: string
  desktopDetail?: string
  icon: ReactNode
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-card border-line-warm bg-surface flex w-full items-center gap-3.5 border p-4 text-left active:scale-[0.995] sm:px-4.5"
    >
      <span className="bg-tint text-accent flex size-11.5 shrink-0 items-center justify-center rounded-2xl">
        {icon}
      </span>

      <span className="flex-1">
        <span className="block text-base font-semibold">
          <span className={desktopTitle ? 'sm:hidden' : ''}>{title}</span>
          {desktopTitle && <span className="hidden sm:inline">{desktopTitle}</span>}
        </span>
        <span className="text-ink-soft mt-0.5 block text-[13px]">
          <span className={desktopDetail ? 'sm:hidden' : ''}>{detail}</span>
          {desktopDetail && <span className="hidden sm:inline">{desktopDetail}</span>}
        </span>
      </span>

      <span aria-hidden="true" className="text-ink-ghost text-lg">
        ›
      </span>
    </button>
  )
}
